// utils/subdomainDetection.js
// Utility to detect booking slug subdomains (e.g., nailzbydream.uhyve.com)

/**
 * Get the booking slug from the current subdomain
 * @returns {string|null} The booking slug if on a subdomain, null otherwise
 * 
 * Examples:
 * - nailzbydream.uhyve.com → 'nailzbydream'
 * - www.uhyve.com → null
 * - uhyve.com → null
 * - localhost:3000 → null
 */
export const getBookingSlugFromSubdomain = () => {
  const host = window.location.host;
  
  // Skip for localhost development
  if (host.includes('localhost') || host.includes('127.0.0.1')) {
    console.log('[Subdomain] Running on localhost, skipping subdomain detection');
    return null;
  }

  const parts = host.split('.');
  
  // For uhyve.com format: ['subdomain', 'uhyve', 'com'] or ['www', 'uhyve', 'com']
  // For production with www: parts.length >= 3
  if (parts.length >= 3) {
    const subdomain = parts[0].toLowerCase();
    const domain = parts[1].toLowerCase();
    
    // Check if it's actually uhyve.com domain
    if (domain === 'uhyve') {
      // Skip common subdomains that aren't booking slugs
      const skipSubdomains = ['www', 'api', 'app', 'admin', 'staging', 'dev'];
      
      if (skipSubdomains.includes(subdomain)) {
        console.log('[Subdomain] Skipping reserved subdomain:', subdomain);
        return null;
      }
      
      console.log('[Subdomain] Detected booking slug:', subdomain);
      return subdomain;
    }
  }
  
  console.log('[Subdomain] No booking slug subdomain detected for host:', host);
  return null;
};

/**
 * Check if we're on a booking slug subdomain
 * @returns {boolean}
 */
export const isBookingSlugSubdomain = () => {
  return getBookingSlugFromSubdomain() !== null;
};

/**
 * Get the main app URL (without subdomain)
 * @returns {string}
 */
export const getMainAppUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:3000';
  }
  return 'https://uhyve.com';
};

/**
 * Build a booking URL for a given slug
 * @param {string} slug - The booking slug
 * @returns {string} The full booking URL
 */
export const getBookingUrlForSlug = (slug) => {
  if (process.env.NODE_ENV === 'development') {
    // In development, use the /book/:slug route
    return `http://localhost:3000/book/${slug}`;
  }
  // In production, use subdomain
  return `https://${slug}.uhyve.com`;
};
